import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import nlp from "compromise";

export const HIGHLIGHTER_COLORS = [
  "#FFF700", // 黄色
  "#C6F6F9", // 浅蓝
  "#FFD6E0", // 粉色
  "#B9FBC0", // 浅绿
  "#FFB4A2", // 橙粉
  "#BDB2FF", // 淡紫
  "#FFD6A5", // 浅橙
  "#A0C4FF", // 天蓝
];

interface HighlighterAnswer {
  tag: string;
  index: number;
  word?: string;
}

type HighlightMode = 'user' | 'judge' | 'truth';

interface HighlighterQuestionProps {
  question: string;
  correctAnswer: HighlighterAnswer[];
  userAnswer: HighlighterAnswer[];
  selectedTag: string;
  disabled: boolean;
  mode?: HighlightMode;
  className?: string;
  onHighlightChange: (highlighted: HighlighterAnswer[]) => void;
}

interface HighlighterSelectorProps {
  answer: HighlighterAnswer[];
  selectedTag: string;
  onSelect: (tag: string) => void;
  isReview?: boolean;
  className?: string;
}

export const getUniqueTags = (answer: HighlighterAnswer[]) => {
  return Array.from(new Set(answer.map((item: { tag: string }) => item.tag)));
}

const getWords = (question: string, answer: HighlighterAnswer[]): string[] => {
  // 提取所有答案中的单词/短语
  const answerWords = answer.map(item => item.word!);

  // 按顺序处理文本，保持原有顺序
  const words: string[] = [];
  let currentText = question;

  // 遍历文本，按顺序匹配答案短语
  while (currentText.length > 0) {
    let matched = false;

    // 尝试匹配最长的答案短语
    for (const answerWord of answerWords) {
      const regex = new RegExp(`^\\s*(${answerWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'i');
      const match = currentText.match(regex);

      if (match) {
        // 找到匹配，先处理前面的文本
        const beforeMatch = currentText.substring(0, match.index).trim();
        if (beforeMatch) {
          const beforeWords = beforeMatch.split(/\s+/).filter((word: string) => word.length > 0);
          words.push(...beforeWords);
        }

        // 添加匹配的答案短语
        words.push(match[1]);

        // 更新剩余文本
        currentText = currentText.substring(match.index! + match[0].length);
        matched = true;
        break;
      }
    }

    if (!matched) {
      // 没有匹配到答案短语，处理下一个单词
      const nextSpace = currentText.indexOf(' ');
      if (nextSpace === -1) {
        // 最后一个单词
        words.push(currentText);
        break;
      } else {
        const nextWord = currentText.substring(0, nextSpace);
        words.push(nextWord);
        currentText = currentText.substring(nextSpace + 1);
      }
    }
  }

  return words.filter(word => word.length > 0);
}

const getStandardAnswer = (question: string, answer: HighlighterAnswer[]) => {
  // 使用正则表达式分割，保留标点符号作为独立的token
  const words = getWords(question, answer);
  const usedIndices = new Set<number>();
  const standardAnswer = answer.map((item) => {
    // 如果 item 有 index，直接使用
    if (item.index !== undefined) {
      usedIndices.add(item.index);
      return {
        tag: item.tag,
        index: item.index
      };
    }
    // 如果 item 有 word，在句子中查找该单词的位置
    if (item.word) {
      // 处理标点符号，匹配时忽略标点
      const cleanWord = item.word.replace(/[.,!?;:]+$/, '');
      let wordIndex = -1;

      // 查找第一个未被使用的匹配索引
      for (let idx = 0; idx < words.length; idx++) {
        const cleanWordInText = words[idx].replace(/[.,!?;:]+$/, '');
        if (cleanWordInText.toLowerCase() === cleanWord.toLowerCase() && !usedIndices.has(idx)) {
          wordIndex = idx;
          usedIndices.add(idx);
          break;
        }
      }

      return {
        tag: item.tag,
        index: wordIndex >= 0 ? wordIndex : 0
      };
    }
    // 默认使用数组索引
    return {
      tag: item.tag,
      index: answer.indexOf(item)
    };
  });
  return standardAnswer;
}

const isHighlightAnswerCorrect = (question: string, user: HighlighterAnswer[], answer: HighlighterAnswer[]) => {
  const standardAnswer = getStandardAnswer(question, answer);
  if (user.length !== standardAnswer.length) return false;
  const userSorted = [...user].sort((a, b) => a.index - b.index);
  const answerSorted = [...standardAnswer].sort((a, b) => a.index - b.index);
  return userSorted.every((item, i) =>
    item.index === answerSorted[i].index && item.tag === answerSorted[i].tag
  );
}

const HighlighterQuestion = ({ question, correctAnswer, userAnswer, selectedTag, disabled, mode = 'user', onHighlightChange, className }: HighlighterQuestionProps) => {
  // 使用正则表达式分割，保留标点符号作为独立的token
  const words = getWords(question, correctAnswer);
  const [standardAnswer, setStandardAnswer] = useState<HighlighterAnswer[]>(correctAnswer);

  useEffect(() => {
    if (question && correctAnswer.length > 0) {
      const standardAnswer = getStandardAnswer(question, correctAnswer);
      setStandardAnswer(standardAnswer);
    }
  }, [correctAnswer, question]);

  const handleWordClick = (idx: number) => {
    if (disabled || mode !== 'user') return;
    let highlighted = [...userAnswer];
    const exist = highlighted.find(item => item.index === idx);
    if (exist) {
      highlighted = highlighted.filter(item => item.index !== idx);
    } else {
      highlighted =  [...highlighted, { index: idx, tag: selectedTag }];
    }
    onHighlightChange(highlighted);
  };

  return (<div className={`text-2xl w-full text-[#573F35] ${className}`} style={{ lineHeight: 2 }}>
    {words.map((word: string, idx: number) => {
      const highlightObj = mode === 'truth'
        ? standardAnswer.find(item => item.index === idx)
        : userAnswer.find(item => item.index === idx);

      // judge模式下，判断该词是否被用户高亮
      let judgeBorder: string | undefined;
      if (mode === 'judge') {
        const userTag = userAnswer.find(item => item.index === idx)?.tag;
        const truthTag = standardAnswer.find(item => item.index === idx)?.tag;

        if (truthTag) {
          // 该位置应该有高亮
          if (userTag) {
            // 用户高亮了，判断对错
            judgeBorder = userTag === truthTag
              ? '2px solid #22c55e' // green-500
              : '2px solid #ef4444'; // red-500
          } else {
            // 用户没高亮，标红框
            judgeBorder = '2px solid #ef4444'; // red-500
          }
        } else if (userTag) {
          // 该位置不应该有高亮，但用户高亮了，标红框
          judgeBorder = '2px solid #ef4444'; // red-500
        }
      }

      return (
        <span
          key={idx}
          className={`px-0.5 rounded ${mode === 'user' ? 'cursor-pointer' : ''}`}
          style={{
            backgroundColor: highlightObj
              ? HIGHLIGHTER_COLORS[getUniqueTags(correctAnswer).indexOf(highlightObj.tag)]
              : "transparent",
            border: judgeBorder,
          }}
          onClick={() => handleWordClick(idx)}
        >
          {word}
          {idx !== words.length - 1 && " "}
        </span>
      );
    })}
  </div>)
};

const HighlighterSelector = ({ answer, selectedTag, onSelect, isReview = false, className }: HighlighterSelectorProps) => {

  // useEffect(() => {
  //   if (answer.length > 0) {
  //     onSelect(getUniqueTags(answer)[0]);
  //   }
  // }, [answer]);

  const handleSelect = (tag: string) => {
    onSelect(tag);
  };

  return (
    <div className={`flex md:flex-row flex-col w-fit gap-2 ${className}`}>
      {getUniqueTags(answer).map((tag) => (
        <button
          key={tag}
          className={`
            flex items-center md:w-fit w-full px-3 py-1 rounded-full border
            cursor-pointer border-gray-300 justify-start
            gap-2
            transition
            ${tag === selectedTag
              ? "border-gray-400 bg-gray-50"
              : "bg-gray-100 text-gray-400"}
          `}
          onClick={() => handleSelect(tag)}
        >
          <span
            className={`inline-block w-10 h-4 rounded ${tag !== selectedTag && !isReview ? "opacity-50" : ""}`}
            style={{ backgroundColor: HIGHLIGHTER_COLORS[getUniqueTags(answer).indexOf(tag)] }}
          />
          <span className={`flex-1 text-base text-start ${tag === selectedTag || isReview ? "text-gray-800" : "text-gray-400"}`}>
            {tag}
          </span>
          {
            !isReview && <Check className={`w-5 h-5 text-green-500 ${tag === selectedTag ? "visible" : "invisible"}`} />
          }
        </button>
      ))}
    </div>
  );
};

export { HighlighterSelector, HighlighterQuestion, isHighlightAnswerCorrect };